import React, { useEffect, useState } from 'react';
import './AnalyseAccordion.css';

interface AnalyseAccordionProps {
  title: string;
  defaultOpen?: boolean;
  // Verrouille la section : non depliable, grisee (ex. analyse avant ouverture des plis).
  locked?: boolean;
  lockedHint?: string;
  // Apparence verrouillee (entete grisee + cadenas) MAIS corps deplie et visible.
  // Ex. "Contenu des plis" avant dechiffrage : on voit les cartes, le verrou signale l'etat.
  lockedKeepOpen?: boolean;
  // Mode controle : si `open` est fourni, l'etat est pilote par le parent via onToggle.
  open?: boolean;
  onToggle?: (next: boolean) => void;
  children: React.ReactNode;
}

const AnalyseAccordion: React.FC<AnalyseAccordionProps> = ({
  title,
  defaultOpen = true,
  locked = false,
  lockedHint,
  lockedKeepOpen = false,
  open,
  onToggle,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen && !locked);
  // Filet pour les accordeons NON controles : se replier quand on les verrouille.
  useEffect(() => {
    if (locked && !lockedKeepOpen) setInternalOpen(false);
  }, [locked, lockedKeepOpen]);
  const isControlled = open !== undefined;
  // lockedKeepOpen : verrouille visuellement mais on garde le corps ouvert.
  const isOpen = (locked && lockedKeepOpen)
    ? true
    : ((isControlled ? !!open : internalOpen) && !locked);

  const toggle = () => {
    if (locked) return;
    if (isControlled) onToggle?.(!isOpen);
    else setInternalOpen((prev) => !prev);
  };

  return (
    <div className={`analyse-accordion${locked ? ' analyse-accordion--locked' : ''}`}>
      {/* Meme design d'entete que les sections de l'onglet Questions/reponses (.section-header) */}
      <div
        className="section-header analyse-accordion-header"
        onClick={toggle}
        role="button"
        aria-expanded={isOpen}
        aria-disabled={locked}
      >
        <div className="analyse-accordion-title">
          {locked && (
            <svg className="analyse-accordion-lock" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm3 8H9V6a3 3 0 0 1 6 0v3Z" />
            </svg>
          )}
          <span>{title}</span>
          {locked && lockedHint && <span className="analyse-accordion-hint">{lockedHint}</span>}
        </div>
        {/* Fleche (chevron V) presente sur chaque section : oriente vers le bas si ouvert.
            SVG inline car Font Awesome n'est pas charge dans le projet. */}
        <svg
          className={`analyse-accordion-chevron${isOpen ? ' open' : ''}`}
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
          focusable="false"
        >
          <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {isOpen && <div className="analyse-accordion-body">{children}</div>}
    </div>
  );
};

export default AnalyseAccordion;
